/**
 * Client Portal - Notifications Center
 * Centralized notification management for clients
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Bell, FileText, PenTool, Calendar, MessageSquare,
  Check, CheckCheck, Trash2, ChevronRight, Settings
} from 'lucide-react';

type NotificationType = 'document' | 'signature' | 'message' | 'appointment' | 'billing' | 'system';
type NotificationPriority = 'high' | 'normal' | 'low';

interface Notification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  matterId?: string;
  matterName?: string;
}

export function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | NotificationType>('all');
  const [, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));

    setNotifications([
      {
        id: '1',
        type: 'signature',
        priority: 'high',
        title: 'Signature Required',
        message: 'The Warranty Deed for your property purchase is ready for your signature.',
        timestamp: new Date(Date.now() - 3600000),
        read: false,
        actionUrl: '/sign/1',
        actionLabel: 'Sign Now',
        matterId: 'matter-1',
        matterName: 'Property Purchase - 123 Main St',
      },
      {
        id: '2',
        type: 'document',
        priority: 'normal',
        title: 'New Document Available',
        message: 'Your closing disclosure has been uploaded and is ready for review.',
        timestamp: new Date(Date.now() - 7200000),
        read: false,
        actionUrl: '/documents',
        actionLabel: 'View Document',
        matterId: 'matter-1',
        matterName: 'Property Purchase - 123 Main St',
      },
      {
        id: '3',
        type: 'message',
        priority: 'normal',
        title: 'New Message from Sarah Johnson',
        message: 'The closing documents are ready for your review.',
        timestamp: new Date(Date.now() - 14400000),
        read: true,
        actionUrl: '/messages',
        actionLabel: 'Read Message',
        matterId: 'matter-1',
        matterName: 'Property Purchase - 123 Main St',
      },
      {
        id: '4',
        type: 'appointment',
        priority: 'normal',
        title: 'Appointment Reminder',
        message: 'You have a closing appointment scheduled for Tuesday, January 15 at 2:00 PM.',
        timestamp: new Date(Date.now() - 86400000),
        read: true,
        actionUrl: '/calendar',
        actionLabel: 'View Calendar',
      },
      {
        id: '5',
        type: 'billing',
        priority: 'low',
        title: 'Invoice Paid',
        message: 'Thank you! Your payment of $1,500.00 for Estate Planning services has been received.',
        timestamp: new Date(Date.now() - 172800000),
        read: true,
        matterId: 'matter-2',
        matterName: 'Estate Planning',
      },
      {
        id: '6',
        type: 'system',
        priority: 'low',
        title: 'Profile Updated',
        message: 'Your contact information has been successfully updated.',
        timestamp: new Date(Date.now() - 259200000),
        read: true,
      },
    ]);

    setLoading(false);
  };

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'document': return FileText;
      case 'signature': return PenTool;
      case 'message': return MessageSquare;
      case 'appointment': return Calendar;
      case 'billing': return FileText;
      case 'system': return Bell;
    }
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'document': return 'bg-blue-100 text-blue-600';
      case 'signature': return 'bg-orange-100 text-orange-600';
      case 'message': return 'bg-purple-100 text-purple-600';
      case 'appointment': return 'bg-green-100 text-green-600';
      case 'billing': return 'bg-emerald-100 text-emerald-600';
      case 'system': return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityBadge = (priority: NotificationPriority) => {
    if (priority === 'high') {
      return (
        <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
          Action Required
        </span>
      );
    }
    return null;
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return n.type === filter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const filterOptions: { value: typeof filter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'unread', label: 'Unread' },
    { value: 'signature', label: 'Signatures' },
    { value: 'document', label: 'Documents' },
    { value: 'message', label: 'Messages' },
    { value: 'appointment', label: 'Appointments' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
            <Bell className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
            <p className="text-gray-500">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all as read
            </button>
          )}
          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filterOptions.map(option => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === option.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {option.label}
            {option.value === 'unread' && unreadCount > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                filter === 'unread' ? 'bg-white/20' : 'bg-blue-600 text-white'
              }`}>
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <AnimatePresence>
          {filteredNotifications.map((notification, index) => {
            const Icon = getTypeIcon(notification.type);
            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 border-b border-gray-100 last:border-0 ${
                  !notification.read ? 'bg-blue-50/50' : ''
                }`}
              >
                <div className="flex gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getTypeColor(notification.type)}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </h3>
                          {getPriorityBadge(notification.priority)}
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-blue-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        {notification.matterName && (
                          <p className="text-xs text-gray-400 mt-1">
                            {notification.matterName}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTime(notification.timestamp)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      {notification.actionUrl && (
                        <Link
                          to={notification.actionUrl}
                          onClick={() => markAsRead(notification.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {notification.actionLabel}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      )}
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-gray-600 text-sm hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Mark as read
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filteredNotifications.length === 0 && (
          <div className="p-12 text-center">
            <Bell className="w-16 h-16 mx-auto text-gray-200 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications</h3>
            <p className="text-gray-500">
              {filter === 'unread'
                ? 'You\'ve read all your notifications!'
                : 'You don\'t have any notifications yet.'}
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {notifications.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <PenTool className="w-4 h-4" />
              <span className="text-sm font-medium">Pending Signatures</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {notifications.filter(n => n.type === 'signature' && !n.read).length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-sm font-medium">New Documents</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {notifications.filter(n => n.type === 'document' && !n.read).length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm font-medium">Unread Messages</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {notifications.filter(n => n.type === 'message' && !n.read).length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Upcoming</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {notifications.filter(n => n.type === 'appointment').length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;
